import React, { useState, useEffect } from 'react';
import knowledgeBaseService from '../../services/knowledgeBaseService';

/**
 * Knowledge Base Page component
 * Provides UI for collecting knowledge base data from web or file
 */
const KnowledgeBasePage = () => {
  // Web collection state
  const [webUrl, setWebUrl] = useState('');
  const [fullScan, setFullScan] = useState(false);
  const [forceRecollect, setForceRecollect] = useState(false);
  const [crawlDepth, setCrawlDepth] = useState(2);
  const [maxPages, setMaxPages] = useState(10);
  const [webLoading, setWebLoading] = useState(false);
  const [webResult, setWebResult] = useState(null);
  const [webError, setWebError] = useState(null);
  const [webElapsed, setWebElapsed] = useState(0);

  // File collection state
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileResult, setFileResult] = useState(null);
  const [fileError, setFileError] = useState(null);

  // GitHub collection state
  const [githubQuery, setGithubQuery] = useState('');
  const [githubMaxRepos, setGithubMaxRepos] = useState(5);
  const [githubLanguage, setGithubLanguage] = useState('');
  const [githubCollectDocs, setGithubCollectDocs] = useState(true);
  const [githubForce, setGithubForce] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);
  const [githubResult, setGithubResult] = useState(null);
  const [githubError, setGithubError] = useState(null);

  // Raw data processing state
  const [processingRaw, setProcessingRaw] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  const [processError, setProcessError] = useState(null);

  // Raw metadata state
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataEntries, setMetadataEntries] = useState([]);
  const [metadataError, setMetadataError] = useState(null);

  // Embedding state
  const [embeddingLoading, setEmbeddingLoading] = useState(false);
  const [embeddingResult, setEmbeddingResult] = useState(null);
  const [embeddingError, setEmbeddingError] = useState(null);
  const [embeddingElapsed, setEmbeddingElapsed] = useState(0);

  // Handle web collection form submission
  const handleWebSubmit = async (e) => {
    e.preventDefault();
    if (!webUrl) return;

    setWebLoading(true);
    setWebResult(null);
    setWebError(null);
    setWebElapsed(0);

    try {
      const result = await knowledgeBaseService.collectFromWeb(
        webUrl,
        fullScan,
        crawlDepth || 2,
        maxPages || 10,
        forceRecollect,
        setWebElapsed
      );
      setWebResult(result);
      // Refresh the raw-data table so a freshly collected page shows up
      // immediately (otherwise a successful collect looks like nothing happened).
      fetchMetadata();
    } catch (error) {
      setWebError(error.response?.data?.detail || error.message);
    } finally {
      setWebLoading(false);
    }
  };

  // Handle file collection form submission
  const handleFileSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    setFileLoading(true);
    setFileResult(null);
    setFileError(null);

    try {
      const result = await knowledgeBaseService.collectFromFile(selectedFile);
      setFileResult(result);
    } catch (error) {
      setFileError(error.response?.data?.detail || error.message);
    } finally {
      setFileLoading(false);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  // Handle GitHub collection form submission
  const handleGithubSubmit = async (e) => {
    e.preventDefault();
    if (!githubQuery) return;

    setGithubLoading(true);
    setGithubResult(null);
    setGithubError(null);

    try {
      const result = await knowledgeBaseService.collectFromGithub(
        githubQuery,
        githubMaxRepos,
        githubLanguage,
        githubCollectDocs,
        githubForce
      );
      setGithubResult(result);
    } catch (error) {
      setGithubError(error.response?.data?.detail || error.message);
    } finally {
      setGithubLoading(false);
    }
  };

  // Handle embedding run
  const handleRunEmbedding = async () => {
    setEmbeddingLoading(true);
    setEmbeddingResult(null);
    setEmbeddingError(null);
    setEmbeddingElapsed(0);

    try {
      const result = await knowledgeBaseService.runEmbedding('chunks', 'data/final_chunks', 'embeddings', setEmbeddingElapsed);
      setEmbeddingResult(result);
    } catch (error) {
      setEmbeddingError(error.response?.data?.detail || error.message);
    } finally {
      setEmbeddingLoading(false);
    }
  };

  // Process raw data
  const handleProcessRawData = async () => {
    setProcessingRaw(true);
    setProcessResult(null);
    setProcessError(null);

    try {
      const result = await knowledgeBaseService.processRawData();
      setProcessResult(result);
      // Refresh metadata after processing
      fetchMetadata();
    } catch (error) {
      setProcessError(error.response?.data?.detail || error.message);
    } finally {
      setProcessingRaw(false);
    }
  };

  // Fetch raw metadata
  const fetchMetadata = async () => {
    setMetadataLoading(true);
    setMetadataError(null);

    try {
      const result = await knowledgeBaseService.listRawMetadata();
      setMetadataEntries(result.entries || []);
    } catch (error) {
      setMetadataError(error.response?.data?.detail || error.message);
    } finally {
      setMetadataLoading(false);
    }
  };

  // Fetch metadata when component mounts
  useEffect(() => {
    fetchMetadata();
  }, []);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="mb-6 text-2xl font-bold text-white">Knowledge Base Data Collection</h1>

      {/* Web Collection Section */}
      <div className="mb-8 rounded-lg bg-gray-800 p-6 shadow-lg border border-gray-700">
        <h2 className="mb-4 text-xl font-semibold text-white">🌐 Collect from Web</h2>
        <form onSubmit={handleWebSubmit}>
          <div className="mb-4">
            <label htmlFor="webUrl" className="mb-2 block font-medium text-gray-300">
              Web Page URL
            </label>
            <input
              type="url"
              id="webUrl"
              className="w-full rounded-md border border-gray-600 bg-gray-700 p-2 text-white focus:border-blue-500 focus:ring-blue-500"
              placeholder="https://example.com"
              value={webUrl}
              onChange={(e) => setWebUrl(e.target.value)}
              required
            />
          </div>

          <div className="mb-4 flex items-center">
            <input
              type="checkbox"
              id="fullScan"
              className="mr-2 h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
              checked={fullScan}
              onChange={(e) => setFullScan(e.target.checked)}
            />
            <label htmlFor="fullScan" className="text-gray-300">
              Full website scan (crawl all pages)
            </label>
          </div>

          <div className="mb-4 flex items-center">
            <input
              type="checkbox"
              id="forceRecollect"
              className="mr-2 h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
              checked={forceRecollect}
              onChange={(e) => setForceRecollect(e.target.checked)}
            />
            <label htmlFor="forceRecollect" className="text-gray-300">
              Force re-collect (ignore already-collected URLs)
            </label>
          </div>

          <div className="mb-2 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="crawlDepth" className="mb-2 block font-medium text-gray-300">
                Crawl Depth
              </label>
              <input
                type="number"
                id="crawlDepth"
                className="w-full rounded-md border border-gray-600 bg-gray-700 p-2 text-white focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                min="1"
                max="5"
                value={crawlDepth}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setCrawlDepth(Number.isNaN(v) ? '' : v);
                }}
                disabled={!fullScan}
              />
            </div>
            <div>
              <label htmlFor="maxPages" className="mb-2 block font-medium text-gray-300">
                Max Pages
              </label>
              <input
                type="number"
                id="maxPages"
                className="w-full rounded-md border border-gray-600 bg-gray-700 p-2 text-white focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                min="1"
                max="100"
                value={maxPages}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setMaxPages(Number.isNaN(v) ? '' : v);
                }}
                disabled={!fullScan}
              />
            </div>
          </div>
          <p className="mb-4 text-xs text-gray-400">
            Crawl Depth and Max Pages apply only to a full website scan. For a single page, just enter the URL above and leave "Full website scan" unchecked.
          </p>

          <button
            type="submit"
            className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:bg-blue-400"
            disabled={webLoading}
          >
            {webLoading ? `Collecting${webElapsed ? ` (${webElapsed}s)` : '...'}` : 'Collect from Web'}
          </button>
        </form>

        {webResult && (
          <div className="mt-4 rounded-md bg-blue-900/30 p-3 text-blue-300 border border-blue-800/50">
            {(webResult.pages_saved > 0) && (
              <p>
                Successfully collected data from <strong>{webResult.pages_saved}</strong> page(s).
              </p>
            )}
            {(webResult.pages_skipped > 0) && (
              <p className="mt-1 text-yellow-300">
                {webResult.pages_skipped} page(s) already collected (skipped).
                {webResult.skipped_urls?.length > 0 && (
                  <span className="block text-xs mt-1 text-gray-400">
                    {webResult.skipped_urls.join(', ')}
                  </span>
                )}
                <span className="block mt-1">
                  Enable "Force re-collect" to harvest them again.
                </span>
              </p>
            )}
            {(webResult.no_content_count > 0) && (
              <p className="mt-1 text-yellow-300">
                {webResult.no_content_count} page(s) had no readable content (skipped).
                {webResult.no_content_urls?.length > 0 && (
                  <span className="block text-xs mt-1 text-gray-400">
                    {webResult.no_content_urls.join(', ')}
                  </span>
                )}
                <span className="block mt-1">
                  These pages may render their content with JavaScript, or the URL has no article text.
                </span>
              </p>
            )}
            {(webResult.pages_saved === 0 && webResult.pages_skipped === 0 && webResult.no_content_count === 0 && webResult.error_count === 0) && (
              <p>No pages were collected.</p>
            )}
            {webResult.error_count > 0 && (
              <p className="mt-1 text-red-400">
                {webResult.error_count} page(s) failed:
                {webResult.errors?.map((e) => ` ${e.url} (${e.error})`).join(',')}
              </p>
            )}
          </div>
        )}

        {webError && (
          <div className="mt-4 rounded-md bg-red-900/30 p-3 text-red-400 border border-red-800/50">
            <p>Error: {webError}</p>
          </div>
        )}
      </div>

      {/* File Collection Section */}
      <div className="mb-8 rounded-lg bg-gray-800 p-6 shadow-lg border border-gray-700">
        <h2 className="mb-4 text-xl font-semibold text-white">📁 Collect from File</h2>
        <form onSubmit={handleFileSubmit}>
          <div className="mb-4">
            <label htmlFor="file" className="mb-2 block font-medium text-gray-300">
              Upload Document
            </label>
            <input
              type="file"
              id="file"
              className="w-full rounded-md border border-gray-600 bg-gray-700 p-2 text-white focus:border-blue-500 focus:ring-blue-500"
              onChange={handleFileChange}
              accept=".txt,.pdf,.docx,.xlsx"
              required
            />
            <p className="mt-1 text-sm text-gray-400">
              Supported formats: .txt, .pdf, .docx, .xlsx
            </p>
          </div>

          <button
            type="submit"
            className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:bg-blue-400"
            disabled={fileLoading || !selectedFile}
          >
            {fileLoading ? 'Uploading...' : 'Upload File'}
          </button>
        </form>

        {fileResult && (
          <div className="mt-4 rounded-md bg-blue-900/30 p-3 text-blue-300 border border-blue-800/50">
            <p>
              Successfully uploaded file: {fileResult.file_name || 'Unknown'}
            </p>
          </div>
        )}

        {fileError && (
          <div className="mt-4 rounded-md bg-red-900/30 p-3 text-red-400 border border-red-800/50">
            <p>Error: {fileError}</p>
          </div>
        )}
      </div>

      {/* GitHub Collection Section */}
      <div className="mb-8 rounded-lg bg-gray-800 p-6 shadow-lg border border-gray-700">
        <h2 className="mb-4 text-xl font-semibold text-white">🐙 Collect from GitHub</h2>
        <p className="mb-4 text-sm text-gray-400">
          Search GitHub repositories by keyword and collect their README + docs into the knowledge base.
        </p>
        <form onSubmit={handleGithubSubmit}>
          <div className="mb-4">
            <label htmlFor="githubQuery" className="mb-2 block font-medium text-gray-300">
              Search Query
            </label>
            <input
              type="text"
              id="githubQuery"
              className="w-full rounded-md border border-gray-600 bg-gray-700 p-2 text-white focus:border-blue-500 focus:ring-blue-500"
              placeholder="e.g. water treatment python"
              value={githubQuery}
              onChange={(e) => setGithubQuery(e.target.value)}
              required
            />
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="githubMaxRepos" className="mb-2 block font-medium text-gray-300">
                Max Repos
              </label>
              <input
                type="number"
                id="githubMaxRepos"
                className="w-full rounded-md border border-gray-600 bg-gray-700 p-2 text-white focus:border-blue-500 focus:ring-blue-500"
                min="1"
                max="30"
                value={githubMaxRepos}
                onChange={(e) => setGithubMaxRepos(parseInt(e.target.value) || 5)}
              />
            </div>
            <div>
              <label htmlFor="githubLanguage" className="mb-2 block font-medium text-gray-300">
                Language Filter (optional)
              </label>
              <input
                type="text"
                id="githubLanguage"
                className="w-full rounded-md border border-gray-600 bg-gray-700 p-2 text-white focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g. python"
                value={githubLanguage}
                onChange={(e) => setGithubLanguage(e.target.value)}
              />
            </div>
          </div>

          <div className="mb-4 flex items-center">
            <input
              type="checkbox"
              id="githubCollectDocs"
              className="mr-2 h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
              checked={githubCollectDocs}
              onChange={(e) => setGithubCollectDocs(e.target.checked)}
            />
            <label htmlFor="githubCollectDocs" className="text-gray-300">
              Also collect /docs files
            </label>
          </div>

          <div className="mb-4 flex items-center">
            <input
              type="checkbox"
              id="githubForce"
              className="mr-2 h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
              checked={githubForce}
              onChange={(e) => setGithubForce(e.target.checked)}
            />
            <label htmlFor="githubForce" className="text-gray-300">
              Force re-collect (ignore already-collected repos)
            </label>
          </div>

          <button
            type="submit"
            className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:bg-blue-400"
            disabled={githubLoading}
          >
            {githubLoading ? 'Searching GitHub...' : 'Search & Collect'}
          </button>
        </form>

        {githubResult && (
          <div className="mt-4 rounded-md bg-blue-900/30 p-3 text-blue-300 border border-blue-800/50">
            {(githubResult.files_saved > 0) && (
              <p>
                Found <strong>{githubResult.repos_found}</strong> repo(s), collected <strong>{githubResult.files_saved}</strong> file(s).
              </p>
            )}
            {(githubResult.skipped_count > 0) && (
              <p className="mt-1 text-yellow-300">
                {githubResult.skipped_count} repo(s) already collected (skipped).
                <span className="block mt-1">Enable "Force re-collect" to harvest them again.</span>
              </p>
            )}
            {(githubResult.files_saved === 0 && githubResult.skipped_count === 0 && githubResult.error_count === 0) && (
              <p>No repos or files were collected.</p>
            )}
            {githubResult.repos?.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-semibold">Collected repositories:</p>
                <ul className="mt-1 text-sm space-y-1">
                  {githubResult.repos.map((repo, i) => (
                    <li key={i} className="text-gray-300">
                      <a href={repo.html_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                        {repo.full_name}
                      </a>
                      {' '}★{repo.stars} — {repo.description || 'No description'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {githubResult.error_count > 0 && (
              <p className="mt-1 text-red-400">
                {githubResult.error_count} error(s): {githubResult.errors?.map((e) => `${e.repo}: ${e.error}`).join('; ')}
              </p>
            )}
          </div>
        )}

        {githubError && (
          <div className="mt-4 rounded-md bg-red-900/30 p-3 text-red-400 border border-red-800/50">
            <p>Error: {githubError}</p>
          </div>
        )}
      </div>

      {/* Raw Data Processing Section */}
      <div className="mb-8 rounded-lg bg-gray-800 p-6 shadow-lg border border-gray-700">
        <h2 className="mb-4 text-xl font-semibold text-white">⚙️ Process Raw Data</h2>
        <p className="mb-4 text-gray-300">
          Process all collected raw data into the knowledge base.
        </p>
        <button
          onClick={handleProcessRawData}
          className="rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:bg-green-400"
          disabled={processingRaw}
        >
          {processingRaw ? 'Processing...' : 'Process Raw Data'}
        </button>

        {processResult && (
          <div className="mt-4 rounded-md bg-green-900/30 p-3 text-green-300 border border-green-800/50">
            <p>
              Processed {processResult.processed_count || 0} raw data entries.
            </p>
          </div>
        )}

        {processError && (
          <div className="mt-4 rounded-md bg-red-900/30 p-3 text-red-400 border border-red-800/50">
            <p>Error: {processError}</p>
          </div>
        )}
      </div>

      {/* Embedding Section */}
      <div className="mb-8 rounded-lg bg-gray-800 p-6 shadow-lg border border-gray-700">
        <h2 className="mb-4 text-xl font-semibold text-white">🧠 Run Embedding</h2>
        <p className="mb-4 text-gray-300">
          Generate vector embeddings for all processed chunks and store them in the vector database.
        </p>
        <button
          onClick={handleRunEmbedding}
          className="rounded-md bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:bg-purple-400"
          disabled={embeddingLoading}
        >
          {embeddingLoading ? `Embedding${embeddingElapsed ? ` (${embeddingElapsed}s)` : '...'}` : 'Run Embedding'}
        </button>

        {embeddingResult && (
          <div className="mt-4 rounded-md bg-purple-900/30 p-3 text-purple-300 border border-purple-800/50">
            {embeddingResult.summary && (
              <>
                <p>
                  Embedding complete: <strong>{embeddingResult.summary.success}</strong> succeeded,
                  {' '}{embeddingResult.summary.failed} failed, {embeddingResult.summary.skipped} skipped
                  {' '}out of {embeddingResult.summary.total} chunks.
                </p>
                {embeddingResult.summary.errors?.length > 0 && (
                  <p className="mt-1 text-red-400">
                    Errors: {embeddingResult.summary.errors.join('; ')}
                  </p>
                )}
              </>
            )}
            {!embeddingResult.summary && embeddingResult.message && (
              <p>{embeddingResult.message}</p>
            )}
          </div>
        )}

        {embeddingError && (
          <div className="mt-4 rounded-md bg-red-900/30 p-3 text-red-400 border border-red-800/50">
            <p>Error: {embeddingError}</p>
          </div>
        )}
      </div>

      {/* Raw Metadata Section */}
      <div className="rounded-lg bg-gray-800 p-6 shadow-lg border border-gray-700">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">📋 Raw Data Entries</h2>
          <button
            onClick={fetchMetadata}
            className="rounded-md bg-gray-700 px-4 py-2 text-white hover:bg-gray-600 disabled:bg-gray-500"
            disabled={metadataLoading}
          >
            {metadataLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {metadataError && (
          <div className="mt-4 rounded-md bg-red-900/30 p-3 text-red-400 border border-red-800/50">
            <p>Error: {metadataError}</p>
          </div>
        )}

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-300">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-300">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-300">
                  File Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-300">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-300">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-300">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700 bg-gray-800">
              {metadataEntries.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-400">
                    {metadataLoading
                      ? 'Loading entries...'
                      : 'No raw data entries found'}
                  </td>
                </tr>
              ) : (
                metadataEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-700">
                    <td className="whitespace-nowrap px-6 py-4 text-white">{entry.id}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-white">
                      {entry.source_url || entry.source_type || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-white">
                      {entry.file_name || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-white">
                      {entry.file_type || entry.source_type || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-white">
                      {entry.status || 'raw'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-white">
                      {new Date(entry.collected_at).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBasePage;
